import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink } from "lucide-react";

// AC5 — Step-by-step guide rendered in a right Sheet panel.
// Inputs:
//   - tab: full tab config (uses tab.label, tab.guide.steps, tab.noGuideTooltip)
//   - open: boolean
//   - onOpenChange: (bool) => void
export default function GuidePanel({ tab, open, onOpenChange }) {
  const nav = useNavigate();
  if (!tab) return null;
  const hasGuide = tab.hasGuide && tab.guide && Array.isArray(tab.guide.steps) && tab.guide.steps.length;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto p-0"
                    data-testid={`guide-panel-${tab.key}`}>
        <SheetHeader className="p-5 border-b border-stone-200 space-y-1.5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-800 font-bold">
            Hướng dẫn sử dụng
          </div>
          <SheetTitle className="font-serif text-xl text-stone-900">{tab.label}</SheetTitle>
          {!hasGuide && (
            <div className="text-xs text-stone-500 mt-1">
              {tab.noGuideTooltip || "Hướng dẫn sắp ra mắt"}
            </div>
          )}
        </SheetHeader>

        <div className="p-5 space-y-4">
          {hasGuide ? (
            tab.guide.steps.map((s) => (
              <div key={s.no}
                   className="flex gap-3"
                   data-testid={`guide-step-${tab.key}-${s.no}`}>
                <div className="w-7 h-7 rounded-sm bg-emerald-900 text-amber-300
                                flex items-center justify-center text-sm font-bold flex-shrink-0">
                  {s.no}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-stone-900 text-sm">{s.title}</div>
                  <div className="text-sm text-stone-600 mt-1 whitespace-pre-line">{s.body}</div>
                  {s.account && (
                    <Badge variant="outline" className="mt-2 text-[10px]">
                      Tài khoản test: {s.account}
                    </Badge>
                  )}
                  {s.screenshot && (
                    <img src={s.screenshot} alt={s.title}
                         className="mt-2 border border-stone-200 rounded-sm w-full" />
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="bg-stone-50 border border-dashed border-stone-300 rounded-sm p-4 text-sm text-stone-600">
              Module này đã sẵn sàng sử dụng nhưng tài liệu hướng dẫn chi tiết đang được biên soạn.
              Trong thời gian chờ, bạn có thể mở trực tiếp menu để dùng các thao tác cơ bản.
            </div>
          )}
        </div>

        <SheetFooter className="p-4 border-t border-stone-200 gap-2">
          {tab.phases && tab.phases.find(p => p.route && p.readiness === "ready") && (
            <Button
              variant="default"
              data-testid={`guide-open-${tab.key}`}
              onClick={() => {
                const target = tab.phases.find(p => p.route && p.readiness === "ready");
                if (target) { onOpenChange(false); nav(target.route); }
              }}>
              <ExternalLink className="w-4 h-4 mr-1.5" />
              Mở menu
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Đóng</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
